<!-- src/lib/components/QuestionInput.svelte -->
<script lang="ts">
    import TextInput from './TextInput.svelte';
    import PeriodSelect from './PeriodSelect.svelte';
    import DateInput from './DateInput.svelte';
    import BooleanInput from './BooleanInput.svelte';
    import type {Period, Question} from "../lib/cnfCheckerLogic";
    import { fly } from 'svelte/transition';
    import { cubicInOut } from 'svelte/easing';

    let { data, onSubmit }: {
        data: Question;
        onSubmit: (value: string | Period | boolean | null) => void;
    } = $props();

    // State variable to hold the current input value
    let currentValue = $state<string | Period | boolean | null>(null);
    let isValueSet = $state(false);
    let focusTrap: HTMLElement | null = null;
    let questionHeight = $state(0); // Variable to store the current question's height

    // Reset state when question changes
    $effect(() => {
        // Using data.id directly to detect question changes
        data.id; // Reference data.id to track it
        console.log(`Question changed to ID: ${data.id}`);
        currentValue = null;
        isValueSet = false;

        // Reset focus to the focus trap element when question changes
        // This ensures that when user tabs, they'll tab into the input field
        setTimeout(() => {
            if (focusTrap) {
                focusTrap.focus();
            }
        }, 0);
    });

    // Handler for when input value changes
    function handleChange(value: string | Period | boolean | null) {
        currentValue = value;
        isValueSet = value !== null && (typeof value !== 'string' || value.trim() !== '');
    }

    // Handler for the Next button
    function handleNext() {
        if (isValueSet) {
            onSubmit(currentValue);
        }
    }

    // Generate a unique input ID based on the question type and ID
    const inputId = `${data.config.type}-${data.id}`;
</script>

<div class="w-full relative overflow-hidden p-1" >
    <!-- Focus trap element - hidden but focusable -->
    <div 
        tabindex="0" 
        bind:this={focusTrap}
        class="sr-only" 
        aria-hidden="true"
    ></div>

    <div style="height: {questionHeight}px; transition: height 0.3s ease-in-out;">
    <!-- Question content with transition -->
    {#key data.id}
        <div 
            class="p-1 overflow-hidden absolute top-0 left-0 right-0"
            bind:clientHeight={questionHeight}
            in:fly={{x:'100%', duration: 400, easing: cubicInOut }}
            out:fly={{x:'-100%', duration: 300, easing: cubicInOut }}
        >
            <!-- Common label for all input types -->
            <label for={inputId} class="block text-2xl font-bold text-gray-900 mb-3">
                Step {data.counter}: {data.label}
            </label>

            {#if data.config.type === 'text'}
                <TextInput {data} {inputId} onChange={handleChange} />
            {:else if data.config.type === 'period-select'}
                <PeriodSelect {data} {inputId} onChange={handleChange} />
            {:else if data.config.type === 'date'}
                <DateInput {data} {inputId} onChange={handleChange} />
            {:else if data.config.type === 'boolean'}
                <BooleanInput {data} {inputId} onChange={handleChange} />
            {/if}

            <!-- Display hint as a helpful caption -->
            <p class="mt-2 text-sm text-gray-600 italic">
                {data.hint}
            </p>
        </div>
    {/key}
    </div>
    <!-- Debug display of current value -->
<!--    <div class="mt-3 p-2 bg-gray-100 rounded border border-gray-300">-->
<!--        <p class="text-sm font-mono">Debug - Current Value: {JSON.stringify(currentValue)} ({typeof currentValue})</p>-->
<!--    </div>-->

    <!-- Next button positioned below the question content -->
    <div>
        <button 
            type="button" 
            class="mt-4 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!isValueSet}
            onclick={handleNext}
        >
            Next
        </button>
    </div>
</div>
